/**
 * Client-side search/ranking helper for the flow action selector
 * (`#flow-action-select` in EditStep.vue).
 *
 * Scope: pure functions only, no Vue/DOM dependencies, so the behaviour can be
 * unit tested in isolation. Matching is lexical only (no semantic/synonym
 * expansion). Ordering is stable: results with equal score keep their original
 * position in the source `actions` array.
 */

// Default cap on how many results are rendered, for any query including the empty one.
//
// The cap matters as much for the empty query as for a search: every rendered result becomes an
// ElOption wrapped in an ElTooltip, and those are instantiated as soon as the action list is
// populated — before the dropdown is ever opened. Connectors ship up to 2330 actions, so an
// uncapped empty query meant ~2330 popper components created just by picking a connection.
export const MAX_ACTION_RESULTS = 100;

// Field weights: description is primary, name secondary, tooltip lowest.
const FIELD_WEIGHTS = {
  description: 1,
  name: 0.6,
  tooltip: 0.3,
} as const;

// Base scores per match tier (higher = more relevant).
const TIER = {
  EXACT: 1000,
  PREFIX: 800,
  WORD_BOUNDARY: 650,
  SUBSTRING: 600,
  ALL_TOKENS: 400,
  TYPO: 200,
} as const;

type FieldKey = keyof typeof FIELD_WEIGHTS;

interface NormalizedField {
  norm: string;
  tokens: string[];
}

export interface NormalizedAction {
  originalIndex: number;
  action: any;
  fields: Record<FieldKey, NormalizedField>;
}

export interface ActionSearchResult {
  originalIndex: number;
  action: any;
}

/**
 * Normalize text for matching:
 * - split camelCase / letter-digit boundaries into separate words
 * - lowercase
 * - strip diacritics/accents
 * - replace punctuation with spaces
 * - collapse repeated whitespace
 */
export function normalizeText(input: unknown): string {
  if (typeof input !== "string" || input.length === 0) {
    return "";
  }
  return (
    input
      // camelCase / PascalCase -> spaced words (createUser -> create User)
      .replace(/([a-z\d])([A-Z])/g, "$1 $2")
      // acronym followed by word (HTTPServer -> HTTP Server)
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      // letter <-> digit boundaries (base64 -> base 64)
      .replace(/([a-zA-Z])(\d)/g, "$1 $2")
      .replace(/(\d)([a-zA-Z])/g, "$1 $2")
      .toLowerCase()
      // strip accents: decompose then remove combining marks (U+0300–U+036F)
      .normalize("NFD")
      .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
      // punctuation / any non-alphanumeric -> space
      .replace(/[^a-z0-9\s]/g, " ")
      // collapse whitespace
      .replace(/\s+/g, " ")
      .trim()
  );
}

function tokenize(norm: string): string[] {
  return norm.length === 0 ? [] : norm.split(" ");
}

function toField(value: unknown): NormalizedField {
  const norm = normalizeText(value);
  return { norm, tokens: tokenize(norm) };
}

/**
 * Precompute normalized search data for the action list. Call this when the
 * action list changes rather than normalizing on every keystroke.
 */
export function buildActionSearchIndex(actions: any[]): NormalizedAction[] {
  if (!Array.isArray(actions)) {
    return [];
  }
  return actions.map((action, originalIndex) => {
    const spec = action?.actionSpec ?? {};
    return {
      originalIndex,
      action,
      fields: {
        description: toField(spec.description),
        name: toField(spec.name),
        tooltip: toField(spec.tooltip),
      },
    };
  });
}

/**
 * Bounded Levenshtein distance with an early exit once `max` is exceeded.
 * Returns a value > max when the true distance is greater than max.
 */
function boundedEditDistance(a: string, b: string, max: number): number {
  const al = a.length;
  const bl = b.length;
  if (Math.abs(al - bl) > max) {
    return max + 1;
  }
  if (al === 0) return bl;
  if (bl === 0) return al;

  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j += 1) prev[j] = j;

  for (let i = 1; i <= al; i += 1) {
    curr[0] = i;
    let rowMin = curr[0];
    const ac = a.charCodeAt(i - 1);
    for (let j = 1; j <= bl; j += 1) {
      const cost = ac === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) {
      return max + 1;
    }
    const tmp = prev;
    prev = curr;
    curr = tmp;
  }
  return prev[bl];
}

// Small, bounded typo tolerance that scales with token length.
function allowedTypoDistance(len: number): number {
  if (len <= 2) return 0;
  if (len <= 4) return 1;
  return 2;
}

function tokenMatchesWithTypo(
  queryToken: string,
  fieldTokens: string[]
): boolean {
  const max = allowedTypoDistance(queryToken.length);
  if (max === 0) return false;
  return fieldTokens.some(
    (ft) => boundedEditDistance(queryToken, ft, max) <= max
  );
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Score a single field against the normalized query. Returns the highest
 * applicable tier score, or 0 if there is no match.
 */
function scoreField(
  queryNorm: string,
  queryTokens: string[],
  field: NormalizedField
): number {
  const { norm, tokens } = field;
  if (!norm) return 0;

  if (norm === queryNorm) return TIER.EXACT;
  if (norm.startsWith(queryNorm)) return TIER.PREFIX;
  if (norm.includes(queryNorm)) {
    const atBoundary = new RegExp(`(^| )${escapeRegExp(queryNorm)}`).test(norm);
    return atBoundary ? TIER.WORD_BOUNDARY : TIER.SUBSTRING;
  }

  // All query tokens present as substrings, order independent.
  const allPresent = queryTokens.every((qt) => norm.includes(qt));
  if (allPresent) return TIER.ALL_TOKENS;

  // Bounded typo tolerance: every query token is either present or close to a
  // field token.
  const allFuzzy = queryTokens.every(
    (qt) => norm.includes(qt) || tokenMatchesWithTypo(qt, tokens)
  );
  if (allFuzzy) return TIER.TYPO;

  return 0;
}

function scoreAction(
  queryNorm: string,
  queryTokens: string[],
  normalized: NormalizedAction
): number {
  let best = 0;
  (Object.keys(FIELD_WEIGHTS) as FieldKey[]).forEach((key) => {
    const raw = scoreField(queryNorm, queryTokens, normalized.fields[key]);
    if (raw > 0) {
      const weighted = raw * FIELD_WEIGHTS[key];
      if (weighted > best) best = weighted;
    }
  });
  return best;
}

/**
 * Filter and rank a precomputed action index against a raw query string.
 *
 * - Empty/whitespace query returns the first `limit` actions in original order.
 * - Non-empty query returns only matching actions, ranked by relevance and
 *   capped at `limit`. Equal scores keep original order (stable).
 * - Every result retains its original array index; the source array is never
 *   reordered or mutated.
 *
 * `limit` defaults to MAX_ACTION_RESULTS. Pass Infinity to render everything —
 * see the note on MAX_ACTION_RESULTS for why that is rarely what you want.
 */
export function filterAndRankActions(
  index: NormalizedAction[],
  rawQuery: string,
  limit: number = MAX_ACTION_RESULTS
): ActionSearchResult[] {
  if (!Array.isArray(index)) return [];

  const effectiveLimit = Number.isFinite(limit)
    ? Math.max(0, Math.floor(limit))
    : index.length;

  const queryNorm = normalizeText(rawQuery);
  if (!queryNorm) {
    return index.slice(0, effectiveLimit).map(({ originalIndex, action }) => ({
      originalIndex,
      action,
    }));
  }

  const queryTokens = tokenize(queryNorm);
  const scored: Array<ActionSearchResult & { score: number }> = [];
  index.forEach((normalized) => {
    const score = scoreAction(queryNorm, queryTokens, normalized);
    if (score > 0) {
      scored.push({
        originalIndex: normalized.originalIndex,
        action: normalized.action,
        score,
      });
    }
  });

  // Stable sort: score desc, then original index asc for ties.
  scored.sort((a, b) => b.score - a.score || a.originalIndex - b.originalIndex);

  return scored
    .slice(0, effectiveLimit)
    .map(({ originalIndex, action }) => ({ originalIndex, action }));
}
